import React, { useContext } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Link as MuiLink,
  Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import {
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation,
  PAGE_ROUTE_CONTACT,
  PAGE_ROUTE_USER_GUIDE
} from "@kivunova/kivufrontendcommon";
import PublicInfoCtaPanel from "./PublicInfoCtaPanel";
import PublicInfoPageHeader from "./PublicInfoPageHeader";
import PublicMarketingLayout from "./PublicMarketingLayout";
import { FAQ_ITEMS } from "./constants/faqContent";
import FaqAnswer from "./FaqAnswer";
import {
  publicInfoBodySx,
  publicInfoFaqAccordionSx,
  publicInfoFaqAccordionSummarySx,
  publicInfoFaqStackSx,
  publicInfoLinkSx
} from "./constants/publicInfoPageStyles";

const FaqPage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const contactPath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_CONTACT, language, defaultLang)
      : `/${PAGE_ROUTE_CONTACT}`;
  const userGuidePath =
    language != null && defaultLang != null
      ? localizedPath(PAGE_ROUTE_USER_GUIDE, language, defaultLang)
      : `/${PAGE_ROUTE_USER_GUIDE}`;

  return (
    <PublicMarketingLayout>
      <PublicInfoPageHeader
        pageTestId="faq-page"
        eyebrow={t("faq-page-eyebrow", "We're here to help")}
        title={t("faq-page-title", "Frequently asked questions")}
        intro={
          <>
            {t(
              "faq-page-intro-prefix",
              "Quick answers about Kivunova FMS. Want step-by-step guidance?"
            )}{" "}
            <MuiLink
              component={Link}
              to={userGuidePath}
              underline="hover"
              sx={publicInfoLinkSx}
              data-testid="faq-ingress-user-guide"
            >
              {t("faq-page-intro-user-guide-link", "Open the User guide")}
            </MuiLink>
            {t("faq-page-intro-mid", ". Need more help?")}{" "}
            <MuiLink
              component={Link}
              to={contactPath}
              underline="hover"
              sx={publicInfoLinkSx}
            >
              {t("faq-page-intro-contact-link", "Contact us")}
            </MuiLink>
            {t("faq-page-intro-suffix", " or use the floating buttons.")}
          </>
        }
      />

      <Typography
        component="p"
        sx={{ ...publicInfoBodySx, textAlign: "center", mb: 2.5 }}
      >
        {t(
          "faq-page-subintro",
          "Tap a question below—we've kept answers clear and practical so you can get back to running your business."
        )}
      </Typography>

      <Typography
        component="div"
        sx={publicInfoFaqStackSx}
        data-testid="faq-accordion-list"
      >
        {FAQ_ITEMS.map((item, index) => (
          <Accordion
            key={item.id}
            data-testid={`faq-accordion-${item.id}`}
            disableGutters
            elevation={0}
            sx={publicInfoFaqAccordionSx}
            defaultExpanded={index === 0}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon color="primary" />}
              aria-controls={`faq-${item.id}-content`}
              id={`faq-${item.id}-header`}
              sx={publicInfoFaqAccordionSummarySx}
            >
              <Typography
                fontWeight={600}
                sx={{ fontSize: { xs: "0.9375rem", sm: "1rem" } }}
              >
                {t(item.questionKey, item.questionFallback)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pt: 0, pb: 2 }}>
              <FaqAnswer item={item} />
            </AccordionDetails>
          </Accordion>
        ))}
      </Typography>

      <PublicInfoCtaPanel
        title={t("faq-page-cta-title", "Still have questions?")}
        testId="faq-page-cta"
      >
        <Typography variant="body1" sx={publicInfoBodySx}>
          {t(
            "faq-page-cta-body",
            "Our team is happy to help with plans, getting started, and everyday use."
          )}{" "}
          <MuiLink
            component={Link}
            to={contactPath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("faq-page-cta-contact-link", "Get in touch")}
          </MuiLink>
          {t("faq-page-cta-mid", " or review the ")}{" "}
          <MuiLink
            component={Link}
            to={userGuidePath}
            underline="hover"
            sx={publicInfoLinkSx}
          >
            {t("faq-page-cta-guide-link", "User guide")}
          </MuiLink>
          {t("faq-page-cta-suffix", " for step-by-step help.")}
        </Typography>
      </PublicInfoCtaPanel>
    </PublicMarketingLayout>
  );
};

export default FaqPage;
